import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronLeft, ChevronRight, Eye, MessageCircle, Star, CalendarPlus, Send, Search,
  ArrowUpDown, ArrowUp, ArrowDown, Download, ExternalLink, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const getStatusStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (["assigned", "completed", "paid"].includes(s))
    return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20";
  if (s.includes("cancelled") || s.includes("refund") || s.includes("no show"))
    return "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10";
  if (s === "pending")
    return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20";
  return "bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-600/10";
};

function SortHeader({ label, sortKey, currentSort, onSort }) {
  const isActive = currentSort.key === sortKey;
  const Icon = !isActive ? ArrowUpDown : currentSort.order === "asc" ? ArrowUp : ArrowDown;
  
  return (
    <button 
      className="flex items-center gap-1 hover:text-slate-900 transition-colors"
      onClick={() => onSort(sortKey)}
    >
      {label}
      <Icon className={`w-3 h-3 ${isActive ? "text-blue-600" : ""}`} />
    </button>
  );
}

function Pagination({ page, totalPages, onPageChange, t }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100">
      <button
        onClick={() => onPageChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
        data-testid="prev-page"
      >
        <ChevronLeft className="w-4 h-4" /> {t("common.previous")}
      </button>
      <div className="flex items-center gap-1">
        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            onClick={() => onPageChange(i + 1)}
            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
              page === i + 1 ? "bg-[#1A1B2E] text-white" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <button
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50"
        data-testid="next-page"
      >
        {t("common.next")} <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// Extracted as a top-level component to prevent remounting on parent re-renders
function BookingTable({ bookings, type, page, totalPages, onPageChange, searchQuery, onSearchChange, sort, onSort, onOpenModal, t }) {
  const searchRef = useRef(null);

  const canMessage = (booking) => booking.status === "Assigned";
  const hasReview = (booking) => booking.review != null;
  const canSurvey = (booking) => booking.status === "Assigned" && booking.tour_has_passed && !booking.survey;
  const hasSurvey = (booking) => !!booking.survey;

  return (
    <div className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
      {/* Search Bar */}
      <div className="p-4 border-b border-slate-100">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            ref={searchRef}
            placeholder={t("bookings.search")}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
            data-testid="booking-search"
          />
        </div>
      </div>
      
      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</TableHead>
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortHeader label={t("table.city")} sortKey="city" currentSort={sort} onSort={onSort} />
            </TableHead>
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortHeader label={t("table.date")} sortKey="date" currentSort={sort} onSort={onSort} />
            </TableHead>
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.time")}</TableHead>
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortHeader label={t("table.language")} sortKey="language" currentSort={sort} onSort={onSort} />
            </TableHead>
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortHeader label={t("table.status")} sortKey="status" currentSort={sort} onSort={onSort} />
            </TableHead>
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.payment")}</TableHead>
            <TableHead className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("table.action")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.map((b, i) => (
            <TableRow 
              key={b.id} 
              className="table-row-hover cursor-pointer" 
              data-testid={`booking-row-${b.id}`}
              onClick={() => onOpenModal(b, "details")}
            >
              <TableCell className="px-4 py-4 text-sm text-slate-600">{(page - 1) * 6 + i + 1}</TableCell>
              <TableCell className="px-4 py-4 text-sm font-medium text-slate-900">{b.city}</TableCell>
              <TableCell className="px-4 py-4 text-sm text-slate-600">{b.date}</TableCell>
              <TableCell className="px-4 py-4 text-sm text-slate-600">{b.time}</TableCell>
              <TableCell className="px-4 py-4 text-sm text-slate-600">{b.language}</TableCell>
              <TableCell className="px-4 py-4">
                <span className={`status-badge ${getStatusStyle(b.status)}`}>{b.status}</span>
              </TableCell>
              <TableCell className="px-4 py-4">
                <span className={`status-badge ${getStatusStyle(b.payment_status)}`}>{b.payment_status}</span>
              </TableCell>
              <TableCell className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onOpenModal(b, "details")}
                    className="text-slate-400 hover:text-blue-600 transition-colors p-1.5 rounded hover:bg-slate-100"
                    data-testid={`view-booking-${b.id}`}
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => canMessage(b) ? onOpenModal(b, "message") : null}
                    className={`transition-colors p-1.5 rounded ${canMessage(b) ? "text-slate-400 hover:text-blue-600 hover:bg-slate-100" : "text-slate-200 cursor-not-allowed"}`}
                    data-testid={`msg-booking-${b.id}`}
                    disabled={!canMessage(b)}
                    title={canMessage(b) ? "Message customer" : "Only available for assigned bookings"}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => hasReview(b) ? onOpenModal(b, "review") : null}
                    className={`transition-colors p-1.5 rounded ${hasReview(b) ? "text-slate-400 hover:text-amber-600 hover:bg-slate-100" : "text-slate-200 cursor-not-allowed"}`}
                    data-testid={`review-booking-${b.id}`}
                    disabled={!hasReview(b)}
                    title={hasReview(b) ? "View review" : "No review available"}
                  >
                    <Star className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => (canSurvey(b) || hasSurvey(b)) ? onOpenModal(b, "survey") : null}
                    className={`transition-colors p-1.5 rounded ${
                      canSurvey(b) ? "text-slate-400 hover:text-violet-600 hover:bg-slate-100" 
                      : hasSurvey(b) ? "text-violet-500 hover:text-violet-700 hover:bg-slate-100"
                      : "text-slate-200 cursor-not-allowed"
                    }`}
                    data-testid={`survey-booking-${b.id}`}
                    disabled={!canSurvey(b) && !hasSurvey(b)}
                    title={
                      hasSurvey(b) ? "View survey" 
                      : canSurvey(b) ? "Post tour survey" 
                      : "Survey available after tour ends (assigned bookings only)"
                    }
                  >
                    <ClipboardList className="w-4 h-4" />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
      <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} t={t} />
    </div>
  );
}

export default function BookingsPage() {
  const { t } = useTranslation();
  const [activeBookings, setActiveBookings] = useState([]);
  const [pastBookings, setPastBookings] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [activeTotalPages, setActiveTotalPages] = useState(1);
  const [pastTotalPages, setPastTotalPages] = useState(1);
  const [selected, setSelected] = useState(null);
  const [modalTab, setModalTab] = useState("details");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sort, setSort] = useState({ key: "date", order: "desc" });
  
  // Survey state
  const [surveyForm, setSurveyForm] = useState({ overall_experience: 5, group_behavior: 5, route_condition: 5, notes: "" });
  const [submittingSurvey, setSubmittingSurvey] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchBookings = useCallback(async (type, page, setData, setPages) => {
    try {
      const { data } = await api.get(`/bookings`, {
        params: {
          type,
          page,
          search: debouncedSearch,
          sort_by: sort.key,
          sort_order: sort.order
        }
      });
      setData(data.bookings);
      setPages(data.total_pages);
    } catch {
      toast.error(`Failed to load ${type} bookings`);
    }
  }, [debouncedSearch, sort]);

  useEffect(() => {
    fetchBookings("active", activePage, setActiveBookings, setActiveTotalPages);
  }, [activePage, debouncedSearch, sort, fetchBookings]);

  useEffect(() => {
    fetchBookings("past", pastPage, setPastBookings, setPastTotalPages);
  }, [pastPage, debouncedSearch, sort, fetchBookings]);

  const handleSort = useCallback((key) => {
    setSort(prev => ({
      key,
      order: prev.key === key && prev.order === "desc" ? "asc" : "desc"
    }));
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const handleSendMessage = async () => {
    if (!message.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/bookings/${selected.id}/message`, { message });
      toast.success("Message sent successfully!");
      setMessage("");
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const openModal = useCallback((booking, tab = "details") => {
    setSelected(booking);
    setModalTab(tab);
    setMessage("");
    if (tab === "survey" && booking.survey) {
      setSurveyForm(booking.survey);
    } else {
      setSurveyForm({ overall_experience: 5, group_behavior: 5, route_condition: 5, notes: "" });
    }
  }, []);

  const handleSubmitSurvey = async () => {
    if (!selected) return;
    setSubmittingSurvey(true);
    try {
      const { data } = await api.post(`/bookings/${selected.id}/survey`, surveyForm);
      toast.success("Survey submitted successfully!");
      const updatedBooking = { ...selected, survey: data.survey };
      setSelected(updatedBooking);
      // Update in the lists
      setActiveBookings(prev => prev.map(b => b.id === selected.id ? updatedBooking : b));
      setPastBookings(prev => prev.map(b => b.id === selected.id ? updatedBooking : b));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit survey");
    } finally {
      setSubmittingSurvey(false);
    }
  };

  const handleAddToCalendar = async (booking, calType) => {
    if (calType === "ics") {
      try {
        const { data } = await api.get(`/bookings/${booking.id}/ics`);
        const blob = new Blob([data.ics], { type: "text/calendar" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.filename;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Calendar file downloaded!");
      } catch {
        toast.error("Failed to generate calendar file");
      }
    } else if (calType === "google") {
      const time24h = booking.time_24h || "09:00";
      const durationHours = booking.duration_hours || parseInt(booking.duration) || 2;
      const startDate = new Date(booking.date + "T" + time24h + ":00");
      const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);
      
      const formatGCal = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
      const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Tour in ${booking.city}`)}&dates=${formatGCal(startDate)}/${formatGCal(endDate)}&details=${encodeURIComponent(`Type: ${booking.type}\nMeeting Point: ${booking.meeting_point}`)}&location=${encodeURIComponent(booking.meeting_point)}`;
      
      window.open(googleUrl, "_blank");
    }
  };

  const selectedIsAssigned = selected?.status === "Assigned";
  const selectedHasSurvey = !!selected?.survey;
  const selectedCanSurvey = selected?.status === "Assigned" && selected?.tour_has_passed && !selected?.survey;

  return (
    <div className="animate-slideUp space-y-6" data-testid="bookings-page">
      <p className="text-sm text-slate-500">{t("bookings.description")}</p>
      <Tabs defaultValue="active" data-testid="bookings-tabs">
        <TabsList className="bg-white border border-slate-200 p-1 rounded-xl">
          <TabsTrigger value="active" data-testid="tab-active-bookings" className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white">
            {t("bookings.activeBookings")}
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="tab-past-bookings" className="rounded-lg data-[state=active]:bg-[#1A1B2E] data-[state=active]:text-white">
            {t("bookings.pastBookings")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          <BookingTable
            bookings={activeBookings}
            type="active"
            page={activePage}
            totalPages={activeTotalPages}
            onPageChange={setActivePage}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            sort={sort}
            onSort={handleSort}
            onOpenModal={openModal}
            t={t}
          />
        </TabsContent>
        <TabsContent value="past" className="mt-6">
          <BookingTable
            bookings={pastBookings}
            type="past"
            page={pastPage}
            totalPages={pastTotalPages}
            onPageChange={setPastPage}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            sort={sort}
            onSort={handleSort}
            onOpenModal={openModal}
            t={t}
          />
        </TabsContent>
      </Tabs>

      {/* Booking Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">
                  {selected.city} | {selected.duration} | {selected.date} | {selected.time}
                </DialogTitle>
                <DialogDescription className="sr-only">Booking details modal</DialogDescription>
              </DialogHeader>

              <Tabs value={modalTab} onValueChange={setModalTab}>
                <TabsList className="bg-slate-100 p-1 rounded-lg">
                  <TabsTrigger value="details" className="rounded-md text-sm" data-testid="modal-tab-details">{t("bookings.details")}</TabsTrigger>
                  <TabsTrigger 
                    value="review" 
                    className={`rounded-md text-sm ${!selected.review ? "opacity-50" : ""}`} 
                    data-testid="modal-tab-review"
                    disabled={!selected.review}
                  >
                    {t("bookings.review")}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="message" 
                    className={`rounded-md text-sm ${!selectedIsAssigned ? "opacity-50" : ""}`} 
                    data-testid="modal-tab-message"
                    disabled={!selectedIsAssigned}
                  >
                    {t("bookings.messageCustomer")}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="survey" 
                    className={`rounded-md text-sm ${!selectedCanSurvey && !selectedHasSurvey ? "opacity-50" : ""}`} 
                    data-testid="modal-tab-survey"
                    disabled={!selectedCanSurvey && !selectedHasSurvey}
                  >
                    Post Tour Survey
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="mt-4" data-testid="modal-details">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.status")}</span>
                        <div className="mt-1"><span className={`status-badge ${getStatusStyle(selected.status)}`}>{selected.status}</span></div>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.type")}</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.type}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.language")}</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.language}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.participants")}</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.participants}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.meetingPoint")}</span>
                        <p className="text-sm text-slate-900 mt-1">{selected.meeting_point}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.compensation")}</span>
                        <p className="text-sm font-semibold text-slate-900 mt-1">{selected.compensation}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {/* Only show customer details if assigned */}
                      {selectedIsAssigned ? (
                        <>
                          <h4 className="text-sm font-semibold text-slate-900 mb-2">{t("bookings.customerInfo")}</h4>
                          <div>
                            <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.name")}</span>
                            <p className="text-sm text-slate-900 mt-1">{selected.customer?.name}</p>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.phone")}</span>
                            <p className="text-sm text-slate-900 mt-1">{selected.customer?.phone}</p>
                          </div>
                          <div>
                            <span className="text-xs uppercase tracking-wider text-slate-400">{t("bookings.email")}</span>
                            <button 
                              className="text-sm text-blue-600 hover:underline mt-1 block" 
                              data-testid="customer-email-link"
                              onClick={() => setModalTab("message")}
                            >
                              {t("bookings.clickToSend")}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="p-4 bg-slate-50 rounded-lg text-center">
                          <p className="text-sm text-slate-500">Customer details are only visible for assigned bookings</p>
                        </div>
                      )}
                      
                      {/* Calendar buttons - only for assigned bookings */}
                      {selectedIsAssigned && (
                        <div className="pt-4 space-y-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" className="w-full" data-testid="add-to-calendar">
                                <CalendarPlus className="w-4 h-4 mr-2" /> {t("bookings.addToCalendar")}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => handleAddToCalendar(selected, "google")}>
                                <ExternalLink className="w-4 h-4 mr-2" />
                                {t("calendar.addToGoogle")}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAddToCalendar(selected, "ics")}>
                                <Download className="w-4 h-4 mr-2" />
                                {t("calendar.downloadIcs")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="review" className="mt-4" data-testid="modal-review">
                  {selected.review ? (
                    <div className="space-y-4">
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("common.rating")}</span>
                        <div className="flex items-center gap-1 mt-2">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} className={`w-5 h-5 ${s <= selected.review.rating ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-wider text-slate-400">{t("common.review")}</span>
                        <p className="text-sm text-slate-700 mt-2 leading-relaxed bg-slate-50 p-4 rounded-lg">{selected.review.text}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 py-8 text-center">{t("bookings.noReview")}</p>
                  )}
                </TabsContent>

                <TabsContent value="message" className="mt-4 space-y-4" data-testid="modal-message">
                  {selectedIsAssigned ? (
                    <>
                      <div>
                        <span className="text-sm font-medium text-slate-700">{t("bookings.writeMessage")}</span>
                        <Textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                          placeholder="Type your message..."
                          className="mt-2 min-h-[120px]"
                          data-testid="message-textarea"
                        />
                        <p className="text-xs text-slate-400 mt-1 text-right">{message.length}/500 characters</p>
                      </div>
                      <Button
                        onClick={handleSendMessage}
                        disabled={sending || !message.trim()}
                        className="bg-[#1A1B2E] hover:bg-[#2D2E45]"
                        data-testid="send-message-btn"
                      >
                        <Send className="w-4 h-4 mr-2" /> {sending ? t("bookings.sending") : t("bookings.send")}
                      </Button>
                    </>
                  ) : (
                    <div className="p-8 text-center">
                      <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Messaging is only available for assigned bookings</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="survey" className="mt-4 space-y-5" data-testid="modal-survey">
                  {selectedHasSurvey ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 bg-violet-50 border border-violet-100 rounded-lg p-3 mb-4">
                        <ClipboardList className="w-4 h-4 text-violet-600" />
                        <p className="text-sm font-medium text-violet-700">Survey submitted on {new Date(selected.survey.submitted_at).toLocaleDateString()}</p>
                      </div>
                      {[
                        { label: "Overall Experience", key: "overall_experience" },
                        { label: "Group Behavior", key: "group_behavior" },
                        { label: "Route Condition", key: "route_condition" },
                      ].map(({ label, key }) => (
                        <div key={key}>
                          <Label className="text-sm text-slate-600">{label}</Label>
                          <div className="flex items-center gap-1 mt-1">
                            {[1, 2, 3, 4, 5].map((v) => (
                              <Star key={v} className={`w-5 h-5 ${v <= (selected.survey[key] || 0) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                            ))}
                            <span className="text-sm text-slate-500 ml-2">{selected.survey[key]}/5</span>
                          </div>
                        </div>
                      ))}
                      {selected.survey.notes && (
                        <div>
                          <Label className="text-sm text-slate-600">Notes</Label>
                          <p className="text-sm text-slate-700 mt-1 bg-slate-50 p-3 rounded-lg">{selected.survey.notes}</p>
                        </div>
                      )}
                    </div>
                  ) : selectedCanSurvey ? (
                    <div className="space-y-5">
                      <p className="text-sm text-slate-500">Share your feedback about this tour to help improve future experiences.</p>
                      {[
                        { label: "Overall Experience", key: "overall_experience", hint: "How was the overall tour experience?" },
                        { label: "Group Behavior", key: "group_behavior", hint: "How was the group's behavior and cooperation?" },
                        { label: "Route Condition", key: "route_condition", hint: "Rate the condition of the tour route" },
                      ].map(({ label, key, hint }) => (
                        <div key={key}>
                          <Label className="text-sm font-medium">{label}</Label>
                          <p className="text-xs text-slate-400 mb-1">{hint}</p>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((v) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setSurveyForm(f => ({ ...f, [key]: v }))}
                                className="p-0.5"
                                data-testid={`survey-${key}-${v}`}
                              >
                                <Star className={`w-6 h-6 transition-colors ${v <= surveyForm[key] ? "text-amber-400 fill-amber-400" : "text-slate-200 hover:text-amber-200"}`} />
                              </button>
                            ))}
                            <span className="text-sm text-slate-500 ml-2">{surveyForm[key]}/5</span>
                          </div>
                        </div>
                      ))}
                      <div>
                        <Label className="text-sm font-medium">Additional Notes</Label>
                        <Textarea
                          value={surveyForm.notes}
                          onChange={(e) => setSurveyForm(f => ({ ...f, notes: e.target.value.slice(0, 500) }))}
                          placeholder="Any additional feedback or observations..."
                          className="mt-1 min-h-[80px]"
                          data-testid="survey-notes"
                        />
                        <p className="text-xs text-slate-400 mt-1 text-right">{surveyForm.notes.length}/500</p>
                      </div>
                      <Button
                        onClick={handleSubmitSurvey}
                        disabled={submittingSurvey}
                        className="bg-[#1A1B2E] hover:bg-[#2D2E45]"
                        data-testid="submit-survey-btn"
                      >
                        <ClipboardList className="w-4 h-4 mr-2" /> {submittingSurvey ? "Submitting..." : "Submit Survey"}
                      </Button>
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Post tour survey is available after the tour time has passed for assigned bookings</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
